import { Input, Textarea, useDisclosure } from '@heroui/react'
import { useMutation } from '@tanstack/react-query'
import PathwayGenLogo from 'assets/images/pathwayGen.png'
import { TopBar } from 'components/Layout/TopBar'
import { TopBarContent } from 'components/Layout/TopBarProvider'
import { Button } from 'components/ui/Button'
import config from 'config'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import ImportPathwayModal from './PathwayGen/ImportPathwayModal'
import PathwayGenerationLoader from './PathwayGen/PathwayGenerationLoader'

export default function GeneratePathway() {
  const { isOpen: isImportOpen, onOpen: onImportOpen, onClose: onImportClose } = useDisclosure()
  const [jsonContent, setJsonContent] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showGenerationLoader, setShowGenerationLoader] = useState(false)
  const [mode, setMode] = useState('default') // 'default', 'scratch', 'template'
  const [pathwayName, setPathwayName] = useState('')
  const navigate = useNavigate()

  const [useCase, setUseCase] = useState('')
  const [agentSpeech, setAgentSpeech] = useState('')

  const sampleTemplates = {
    'Restaurant Reservations': {
      useCase: 'This AI helps customers book a table at Bella Vista Restaurant. It should collect three key details: the number of guests, the preferred reservation date and time, and the customer\'s contact information. After collecting the details, confirm the reservation and let them know someone will call back within 30 minutes to finalize. The AI should also answer common questions about hours (open Tuesday-Sunday 5-10pm), location (123 Main Street), and menu highlights. If the party is larger than 8 people, transfer the call to speak with a manager.',
      agentSpeech: 'Keep the tone warm, welcoming, and professional — like a friendly restaurant host. Use natural, polite language.\n\nSample phrases:\n• "Hi there, this is Bella Vista Restaurant reservation line. Would you like to book a table?"\n• "Great! How many people will be dining with us?"\n• "What date and time would you prefer?"\n• "Perfect! I have you down for a table for 4 on Friday at 7 PM. We\'ll call you back within 30 minutes to confirm!"',
    },
    'Tech Support': {
      useCase: 'Create a customer support flow for a software company. Start by greeting the customer and asking for their issue. Include paths for common problems like login issues, billing questions, and technical difficulties. Add nodes for escalation to a supervisor if the issue is complex, password resets, and end with a satisfaction check. The AI should be able to handle basic troubleshooting and know when to escalate.',
      agentSpeech: 'Professional, helpful, and patient. Show empathy when customers are frustrated.\n\nSample phrases:\n• "Hi! I\'m here to help with any technical issues you\'re experiencing today."\n• "I understand how frustrating that must be. Let me help you resolve this."\n• "Let me walk you through the steps to fix this..."\n• "Is there anything else I can help you with today?"',
    },
    'Lead Qualification': {
      useCase: 'Design a sales qualification pathway for a B2B software product. Begin with a warm introduction and ask qualifying questions about company size, current challenges, budget range, and decision timeline. Include paths for hot leads (ready to buy), warm leads (need nurturing), and cold leads (not a fit). Schedule demos for qualified prospects and collect contact information. End with appropriate follow-up actions based on lead temperature.',
      agentSpeech: 'Confident, consultative, and professional. Focus on being helpful rather than pushy.\n\nSample phrases:\n• "Hi! I\'m calling to learn more about your business and see if our solution might be a good fit."\n• "Can you tell me about your current process for [relevant area]?"\n• "What challenges are you facing with your current system?"\n• "Based on what you\'ve shared, I think a quick demo would be valuable. When works best for you?"',
    },
  }

  const handleTemplateSelect = (template) => {
    setUseCase(template.useCase)
    setAgentSpeech(template.agentSpeech)
  }

  const formatPrompt = (useCase, agentSpeech) => {
    let prompt = useCase || ''

    if (agentSpeech && agentSpeech.trim()) {
      if (prompt) {
        prompt += '\n\n'
      }
      prompt += `<agent_speech>\nThe following are examples of agent speech:\n${agentSpeech}\n</agent_speech>`
    }

    return prompt
  }

  async function pollStatus(jobId) {
    const maxAttempts = 60
    const pollInterval = 5000

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await $fetch(`v1/pathway/generate/status/${jobId}`)

        if (response.data.ready) {
          if (response.data.error) {
            throw new Error(response.data.error)
          }
          return response.data
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval))
      }
      catch (err) {
        console.error('Status check failed:', err)
        if (err.message === 'Failed to parse .bland file') {
          throw new Error('Failed to generate valid pathway. Please try again or use a different prompt.')
        }
        throw err
      }
    }
    throw new Error('Processing timed out. Please try again.')
  }

  const createPathwayMutation = useMutation({
    mutationFn: async (data) => {
      if (data.type === 'json') {
        return await $fetch('v1/convo_pathway/create', {
          method: 'POST',
          body: {
            name: data.pathwayName || 'New Pathway',
            description: 'A new pathway created from JSON',
            nodes: jsonContent?.nodes,
            edges: jsonContent?.edges,
          },
        })
      }
      else if (data.type === 'scratch') {
        return await $fetch('v1/convo_pathway/create', {
          method: 'POST',
          body: {
            name: data.pathwayName || 'New Pathway',
            description: 'From scratch',
          },
        })
      }
      else if (data.type === 'generate') {
        const formattedPrompt = formatPrompt(data.useCase, data.agentSpeech)

        const { data: submissionData } = await $fetch('v1/pathway/generate', {
          method: 'POST',
          body: {
            input_prompt: formattedPrompt,
            input_name: data.pathwayName || 'New Pathway',
          },
        })

        const { jobId } = submissionData
        if (!jobId) {
          throw new Error('No job ID returned from server')
        }

        return await pollStatus(jobId)
      }
    },
    onSuccess: (result) => {
      const pathway_id = result?.data?.pathway_id ?? result?.pathway_id
      if (pathway_id) {
        toast.success('Pathway generated successfully!')
        setTimeout(() => {
          const redirectUrl = `/dashboard/convo-pathways?id=${pathway_id}&format=true`
          setTimeout(() => {
            navigate(redirectUrl)
          }, 500)
        }, 150)
      }
    },
    onError: (error) => {
      if (error?.data?.errors) {
        toast.error(error.data.errors[0]?.message)
      }
      else {
        const errorMessage = error.message || 'An error occurred while creating the pathway'
        toast.error(errorMessage)
      }
      setIsLoading(false)
      setShowGenerationLoader(false)
    },
  })

  const handleModalSubmit = async (data) => {
    setIsLoading(true)
    createPathwayMutation.mutate(data)
  }

  const handleScratchMode = () => {
    setMode('scratch')
    setPathwayName('')
  }

  const handleBackToDefault = () => {
    setMode('default')
    setPathwayName('')
    setUseCase('')
    setAgentSpeech('')
  }

  const handleGenerate = () => {
    if (mode === 'scratch') {
      if (!pathwayName.trim()) {
        toast.error('Please enter a pathway name')
        return
      }
      setIsLoading(true)
      createPathwayMutation.mutate({ type: 'scratch', pathwayName })
    }
    else {
      // Default generate mode
      if (!pathwayName.trim()) {
        toast.error('Please enter a pathway name')
        return
      }
      setIsLoading(true)
      setShowGenerationLoader(true)
      createPathwayMutation.mutate({
        type: 'generate',
        pathwayName,
        useCase,
        agentSpeech,
      })
    }
  }

  const getPageTitle = () => {
    switch (mode) {
      case 'scratch':
        return 'Create from Scratch'
      default:
        return 'Generate Pathway'
    }
  }

  const getDescription = () => {
    switch (mode) {
      case 'scratch':
        return 'Start with a blank canvas and build your pathway step by step'
      default:
        return 'Create a new pathway from a use case or a script, or a pathway JSON, or generate one from scratch'
    }
  }

  return (
    <div>
      <TopBarContent>
        <TopBar.Title>{getPageTitle()}</TopBar.Title>
        {mode === 'default' && (
          <TopBar.Actions>
            <Button
              variant="secondary"
              size="sm"
              onClick={onImportOpen}
            >
              Import Pathway
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleScratchMode}
            >
              Create from Scratch
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => window.open(`${config.ENV === 'development' ? 'http://localhost:3001' : 'https://app.bland.ai'}/showcase/pathway`, '_blank')}
            >
              Templates
            </Button>
          </TopBar.Actions>
        )}
      </TopBarContent>

      <div className="bg-white">
        {/* Main Content */}
        <main className="mx-auto max-w-5xl px-4 pt-4 pb-8">
          {/* Back Button */}
          {mode !== 'default' && (
            <div className="mb-6">
              <Button
                variant="light"
                size="md"
                startContent={<ArrowLeft size={18} />}
                onPress={handleBackToDefault}
                className={`
                  text-gray-600 transition-colors
                  hover:bg-gray-100 hover:text-gray-900
                `}
              >
                Back to Generate Pathway
              </Button>
            </div>
          )}

          <div className="mb-8 flex flex-col items-center text-center">
            <img
              src={PathwayGenLogo}
              alt="Pathway Gen Icon"
              className="mb-4 h-16 w-16"
            />
            <h2 className="mb-2 text-2xl font-semibold text-[#030712]">{getPageTitle()}</h2>
            <p className="max-w-md text-[#4b5563]">
              {getDescription()}
            </p>
          </div>

          <div className={`
            rounded-lg border border-[#e5e7eb] bg-white p-6 shadow-lg
          `}
          >
            <Input
              placeholder="Enter Pathway Name"
              radius="sm"
              variant="bordered"
              className="mb-6 bg-[#f9fafb]"
              value={pathwayName}
              onChange={e => setPathwayName(e.target.value)}
            />

            {mode === 'default' && (
              <div className="flex flex-col gap-y-6">
                <div>
                  <label className={`
                    mb-2 block text-sm font-medium text-[#030712]
                  `}
                  >
                    Define the Use Case
                  </label>
                  <Textarea
                    placeholder="Describe what this AI call is supposed to do. Be specific — e.g., 'Take orders for a restaurant,' 'Confirm appointment times,' or 'Answer common product questions.' Include any details the AI needs to know, like the business name, hours, or service type."
                    radius="sm"
                    variant="bordered"
                    minRows={8}
                    className="min-h-[150px] bg-[#f9fafb]"
                    value={useCase}
                    onChange={e => setUseCase(e.target.value)}
                  />
                </div>

                <div className="border-t border-[#e5e7eb] pt-6">
                  <label className={`
                    mb-2 block text-sm font-medium text-[#030712]
                  `}
                  >
                    Provide Examples of Agent Speech
                  </label>
                  <Textarea
                    placeholder="Describe the personality and tone the AI should use — casual, friendly, professional, confident, etc. You can also give short phrases it should say, like greetings or closings."
                    radius="sm"
                    variant="bordered"
                    minRows={6}
                    className="min-h-[100px] bg-[#f9fafb]"
                    value={agentSpeech}
                    onChange={e => setAgentSpeech(e.target.value)}
                  />
                </div>

                {/* Sample Templates Section - moved to bottom */}
                <div className="">
                  <div className="flex items-center gap-3 text-xs">
                    <span className="whitespace-nowrap text-[#6b7280]">💡 Quick start:</span>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(sampleTemplates).map(([title, template]) => (
                        <button
                          key={title}
                          type="button"
                          onClick={() => handleTemplateSelect(template)}
                          className={`
                            rounded border border-gray-200 bg-[#f8fafc] px-2
                            py-1 text-xs whitespace-nowrap text-[#4b5563]
                            transition-all duration-150
                            hover:bg-[#f1f5f9] hover:text-[#030712]
                          `}
                        >
                          {title}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className={`
              mt-6 flex items-center justify-between border-t border-[#e5e7eb]
              pt-4
            `}
            >
              <div className="flex gap-2">
              </div>
              <Button
                color="primary"
                radius="sm"
                size="md"
                className={`
                  bg-[#030712] shadow-md transition-transform
                  hover:scale-105
                `}
                onPress={handleGenerate}
                isLoading={isLoading}
              >
                {mode === 'scratch' ? 'Create Pathway' : 'Generate'}
              </Button>
            </div>
          </div>
        </main>
      </div>

      {/* Import Modal */}
      <ImportPathwayModal
        isOpen={isImportOpen}
        onClose={onImportClose}
        onSubmit={handleModalSubmit}
        isLoading={isLoading}
        jsonContent={jsonContent}
        setJsonContent={setJsonContent}
      />

      {/* Generation Loader */}
      {showGenerationLoader && (
        <PathwayGenerationLoader
          pathwayName={pathwayName}
          useCase={useCase}
          agentSpeech={agentSpeech}
        />
      )}
    </div>
  )
}
